import { TextareaHTMLAttributes, forwardRef } from 'react';
import { cn } from './cn';
import {
  textAreaStyles,
  textAreaContainerStyles,
} from './Textarea.styles';

export type TextAreaSize = 'medium' | 'small' | 'tiny' | 'md' | 'sm' | 'lg';

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  status?: 'default' | 'error' | 'success';
  size?: TextAreaSize;
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  /** Label displayed above the textarea */
  label?: string;
  /** Hint text displayed below the textarea */
  hint?: string;
}

const textAreaSizeAliasMap: Record<TextAreaSize, 'medium' | 'small' | 'tiny'> = {
  medium: 'medium',
  small: 'small',
  tiny: 'tiny',
  md: 'medium',
  sm: 'small',
  lg: 'medium',
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      status = 'default',
      size = 'medium',
      resize = 'vertical',
      disabled,
      label,
      hint,
      ...props
    },
    ref
  ) => {
    const resolvedSize = textAreaSizeAliasMap[size] ?? 'medium';
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label className="text-[11px] font-semibold text-[var(--color-gray-800)]">
            {label}
          </label>
        )}
        <div className={textAreaContainerStyles({ size: resolvedSize })}>
          <textarea
            ref={ref}
            className={cn(
              textAreaStyles({ status, size: resolvedSize, resize }),
              className
            )}
            disabled={disabled}
            {...props}
          />
        </div>
        {hint && (
          <span className="text-[11px] text-[var(--color-gray-600)]">
            {hint}
          </span>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

/** Lowercase alias for backwards-compat with consumers that imported `Textarea`. */
export const Textarea = TextArea;
export type TextareaProps = TextAreaProps;
export type TextareaSize = TextAreaSize;
